import { assetManifest } from "../assets/manifest";
import { GAME_FRAME } from "../config/gameplay";
import type { GameSnapshot } from "../game/runtime/types";
import type { OrbitForgeRuntime } from "../game/runtime/runtime";

interface LuckySparkDomCache {
  hidden: boolean;
  left: string;
  top: string;
  timerState: string;
  ariaLabel: string;
  dashOffset: string;
}

export function createLuckySparkLayer(runtime: OrbitForgeRuntime): HTMLElement {
  const layer = document.createElement("div");
  layer.className = "of-lucky-spark-layer";
  const cache: LuckySparkDomCache = {
    hidden: true,
    left: "",
    top: "",
    timerState: "",
    ariaLabel: "Claim Lucky Spark",
    dashOffset: "0"
  };

  const button = document.createElement("button");
  button.className = "of-lucky-spark";
  button.type = "button";
  button.hidden = true;
  button.setAttribute("aria-label", "Claim Lucky Spark");
  button.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    runtime.dispatch({ type: "claim-lucky-spark" });
  });
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  const timer = createTimerRing();
  const art = document.createElement("img");
  art.className = "of-lucky-spark-art";
  art.src = assetManifest.luckySpark.url;
  art.alt = "";
  art.width = assetManifest.luckySpark.width;
  art.height = assetManifest.luckySpark.height;
  art.draggable = false;
  button.append(timer.svg, art);
  layer.append(button);

  runtime.subscribe((snapshot) => updateLuckySpark(button, timer.progress, cache, snapshot));
  return layer;
}

function createTimerRing(): { readonly svg: SVGSVGElement; readonly progress: SVGCircleElement } {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("of-lucky-spark-timer");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("aria-hidden", "true");

  const track = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  track.classList.add("of-lucky-spark-timer-track");
  track.setAttribute("cx", "50");
  track.setAttribute("cy", "50");
  track.setAttribute("r", "43");

  const progress = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  progress.classList.add("of-lucky-spark-timer-progress");
  progress.setAttribute("cx", "50");
  progress.setAttribute("cy", "50");
  progress.setAttribute("r", "43");
  progress.setAttribute("pathLength", "1");
  progress.setAttribute("stroke-dasharray", "1");
  progress.setAttribute("stroke-dashoffset", "0");

  svg.append(track, progress);
  return { svg, progress };
}

function updateLuckySpark(button: HTMLButtonElement, progressRing: SVGCircleElement, cache: LuckySparkDomCache, snapshot: GameSnapshot): void {
  const spark = snapshot.events.luckySpark;
  setHidden(button, !spark.active, cache);
  if (!spark.active) {
    return;
  }

  setStyle(button, "left", `${(spark.frameX / GAME_FRAME.width) * 100}%`, cache);
  setStyle(button, "top", `${(spark.frameY / GAME_FRAME.height) * 100}%`, cache);
  setTimerState(button, spark.remainingSeconds <= 3 ? "low" : "ready", cache);
  setAriaLabel(button, `Claim Lucky Spark, ${Math.ceil(spark.remainingSeconds)} seconds left`, cache);
  setDashOffset(progressRing, `${(1 - spark.progress).toFixed(3)}`, cache);
}

function setHidden(button: HTMLButtonElement, hidden: boolean, cache: LuckySparkDomCache): void {
  if (cache.hidden !== hidden) {
    button.hidden = hidden;
    cache.hidden = hidden;
  }
}

function setStyle(button: HTMLButtonElement, property: "left" | "top", value: string, cache: LuckySparkDomCache): void {
  if (cache[property] === value) {
    return;
  }

  button.style[property] = value;
  cache[property] = value;
}

function setTimerState(button: HTMLButtonElement, value: string, cache: LuckySparkDomCache): void {
  if (cache.timerState !== value) {
    button.dataset.timer = value;
    cache.timerState = value;
  }
}

function setAriaLabel(button: HTMLButtonElement, value: string, cache: LuckySparkDomCache): void {
  if (cache.ariaLabel !== value) {
    button.setAttribute("aria-label", value);
    cache.ariaLabel = value;
  }
}

function setDashOffset(progressRing: SVGCircleElement, value: string, cache: LuckySparkDomCache): void {
  if (cache.dashOffset !== value) {
    progressRing.setAttribute("stroke-dashoffset", value);
    cache.dashOffset = value;
  }
}
